<template>
  <Loader :value="contactPromise" :message="$metaInfo.title" class="custom-contract-create">
    <div class="container view">
      <HeaderComponent :title="$metaInfo.title" />
      <Paywall :value="$store.state.addons" items="custom-contract">
        <template #content>
          <Pricing value="custom-contract">
            <template #actions>
              <ButtonComponent
                :label="$t('activate')"
                :icon="['fal', 'plus']"
                theme="primary"
                :labels="{ confirm: $t('confirm') }"
                :icons="{ confirm: ['fas', 'question-circle'] }"
                confirm
                @click="addon"
                :promise="addonPromise"
              />
            </template>
          </Pricing>
        </template>
        <template #default>
          <Card tag="form" @submit.prevent="submit" class="custom-contract-create__form">
            <template #default>
              <div class="custom-contract-create__inputs">
                <FileComponent
                  v-model="data.template.document"
                  data-field="document"
                  :label="`${labels.contract} (${$t('maximumShort')} 25MB)`"
                  :upload-options="{ acl: 'private' }"
                  :upload="upload"
                  required
                  accept=".pdf"
                />
                <FileComponent
                  v-model="data.template.attachments"
                  :label="`${$tc('attachment', 2)} (${$t('maximumShort')} 25MB)`"
                  accept=".pdf"
                  :upload-options="{ acl: 'private' }"
                  :upload="upload"
                  multiple
                  data-field="attachment"
                >
                  <template #below>
                    <Alert class="mt-1">
                      <div>{{ $t('attachmentsSigningUploadWarning') }}</div>
                    </Alert>
                  </template>
                </FileComponent>
              </div>
              <InputComponent
                :label="labels.signingAuthority"
                required
                :is-valid="validator.fields.signingAuthority.valid"
                :validation-message="validator.fields.signingAuthority.error"
                class="mt-2"
              >
                <UserSelect
                  v-model="data.signingAuthority"
                  search
                  multiple
                  :items="fetchUsers"
                  :title="$t('selectx', { x: labels.signingAuthority.toLowerCase() })"
                  class="elder-input__element"
              /></InputComponent>
              <hr class="mt-4 mb-3" />
              <div>
                <h2>{{ $tc('buyer') }}</h2>
                <Applicant v-model="data.data" :rules="rulesOverride" :disabled-fields="disabledApplicantFields" />
              </div>
            </template>
            <template #footer>
              <ButtonComponent
                :label="$t('sendToSigning')"
                theme="primary"
                type="submit"
                :promise="promises.mutate"
                :disabled="!validator.passes"
                :icon="['fal', 'file-contract']"
              />
            </template>
          </Card>
        </template>
      </Paywall>
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import ModelMixin from '@/mixins/Model'
import PersonModel from '@/models/Person'
import { mapState, mapActions } from 'vuex'
import { PrefixObjectKeys, ObjectToDotNotation } from '@/utils'
import { mergeDeepRight } from 'ramda'
import Bus from '@/bus'
import UserApi from '@/api/User'
import ContactApi from '@/api/Contact'
import { VueComponentUpload as upload } from '@kvass/storage'

import HeaderComponent from '@/components/Header'
import { InputComponent } from 'vue-elder-input'
import { FileComponent } from '@kvass/file'
import Applicant, { rules as ApplicantRules, labels as ApplicantLabels } from '@/components/Contract/Applicant'
import Card from '@/components/Card'
import UserSelect from '@/components/User/Select/Modal'
import { Paywall } from '@/components/Upsell'
import Pricing from '@/components/Pricing'
import { AlertComponent as Alert } from 'vue-elder-alert'

const RulesOverride = {
  'applicant.socialSecurityNumber': `socialSecurityNumber|different:coApplicant.socialSecurityNumber`,
  'coApplicant.socialSecurityNumber': `socialSecurityNumber`,
}

export default {
  mixins: [
    ModelMixin({
      source: null,
      model: {
        template: {
          document: null,
          attachments: [],
        },
        data: {
          applicant: PersonModel(),
          hasCoApplicant: false,
          coApplicant: PersonModel(),
          customFields: null,
        },
        signingAuthority: [],
      },
      transform: {
        signingAuthority: v => {
          if (!v) return []
          return v.map(x => x.id)
        },
      },
    }),
    Validator({
      rules: {
        data: {
          ...ApplicantRules,
          ...RulesOverride,
        },
        'template.document': 'required',
        signingAuthority: 'required',
      },
      labels: 'labels',
      data: 'data',
    }),
  ],
  props: {
    contact: String,
  },
  watch: {
    contact: {
      handler: 'fetchContact',
      immediate: true,
    },
  },
  data() {
    return {
      contactPromise: null,
      addonPromise: null,
      rulesOverride: RulesOverride,
      disabledApplicantFields: [],
    }
  },
  computed: {
    ...mapState('Contract', ['item', 'promises']),
    labels() {
      return {
        contract: this.$tc('contract'),
        signingAuthority: this.$t('signingAuthority'),
        ...PrefixObjectKeys('data.', ApplicantLabels),
      }
    },
  },
  methods: {
    ...mapActions('Contract', ['mutate']),
    submit() {
      let data = this.getData()

      if (!data.data.hasCoApplicant) delete data.data.coApplicant

      return this.mutate([
        data,
        {
          mutation: 'CustomContractCreate',
          strategy: 'none',
        },
      ]).then(() => {
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        if (this.contact) return this.$router.push({ name: 'contact-dashboard', params: { id: this.contact } })
        else this.$router.push({ name: 'contacts' })
      })
    },
    fetchUsers(filter) {
      filter = { ...filter, type: 'user' }
      return UserApi.get({ filter }, { query: 'UserSearch' })
    },
    fetchContact() {
      if (!this.contact) return
      return (this.contactPromise = ContactApi.contractPrefill({ id: this.contact }).then(applicant => {
        this.data = mergeDeepRight(this.data, { data: { applicant } })
        this.disabledApplicantFields = Object.entries(ObjectToDotNotation(this.data.data))
          .filter(([k, v]) => v !== null && ['email', 'phone', 'name'].some(e => k.includes(e)))
          .map(([k]) => k)
      }))
    },
    addon() {
      return (this.addonPromise = this.$store.dispatch('subscription', { item: 'custom-contract', state: true }))
    },
    async upload(file, onProgress, options) {
      if (file.size > 25000000) {
        Bus.$emit('notification', { theme: 'error', content: `${this.$t('maximumShort')} 25MB` })
        throw new Error('File too large')
      }
      return upload(file, onProgress, options)
    },
  },
  mounted() {
    if (!this.data.signingAuthority.length) this.data.signingAuthority = [this.$store.state.user]
  },
  metaInfo() {
    return {
      title: this.$t('createContract'),
    }
  },
  components: {
    InputComponent,
    HeaderComponent,
    Card,
    FileComponent,
    Applicant,
    UserSelect,
    Paywall,
    Pricing,
    Alert,
  },
}
</script>

<style lang="scss">
.custom-contract-create {
  background-color: $background-grey;

  .container {
    max-width: 1000px;
  }

  .pricing-card {
    position: sticky;
    top: 200px;
  }

  &__form {
    margin: 0 auto;
  }

  &__inputs {
    display: grid;
    align-items: center;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: $form-gap-row / 2 $form-gap-column;
    align-items: flex-start;
  }

  [data-field='references'] {
    .elder-input__element {
      padding: 0;
    }
    .select-checklist__items {
      padding: 1.1em;
      max-height: 250px;
      overflow-y: auto;

      @include scrollShadow();
    }
  }
}
</style>
